import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import type { IClientSectionSettings } from 'legacy/lut/features/partner360/sectionSettings/models/iSectionSettingsState'
import { CommitmentReminderActionTypeEnum } from 'legacy/lut/services/partner360/commitment/models/ICommitmentReminder'

import { isSubCategoryMandatory } from 'partner360/entities/planning'
import {
  normalizePlanReviewActionParams,
  resolvePlanReviewCategoryActionParam,
} from 'partner360/lib/planReviewActionParams'

import type {
  IFinancialCockpitActualsAndTargetsSetting,
  IFinancialCockpitPendingAction,
} from '../../../../api/types'
import { OVERALL_CATEGORY } from '../../../../lib'
import { FULL_YEAR_QUARTER } from '../../../../lib/revenueAndSob/constants'
import { isActualsFinSobRow } from '../../../../lib/revenueAndSob/isActualsFinSobRow'
import type { IPartnerFinSobRow } from '../../../../model/types'

/** What the section can say about the offering, beyond the rows themselves. */
export type TMissingSobState =
  /** Categories are named below. */
  | 'missing'
  /** The catalog carries no SOB-collected category for the offering: nothing can be named. */
  | 'noCatalog'
  /** Every category the offering collects SOB on already carries a figure for the period. */
  | 'allSubmitted'

export interface IMissingSobCategory {
  /** The product category, as the catalog and the planning tab name it. */
  category: string
  /** `CategoryOrder`, so the card lists categories in the planning tab's own order. */
  order: number
  /** The payload's own `jbp-pat-sob` action for the category, where it carries one. */
  action: IFinancialCockpitPendingAction | null
  /** Quarters of `fYear` that do carry an SOB figure for it — what is already in, if anything. */
  quartersWithSob: number[]
}

export interface IMissingSobCategoriesResult {
  categories: IMissingSobCategory[]
  state: TMissingSobState
}

interface IParams {
  /** The category catalog, `ActualsAndTargetsSettings` as the payload sends it. */
  settings: IFinancialCockpitActualsAndTargetsSetting[]
  /** The partner's canonical Fin/SOB rows — actuals, targets and quotas together. */
  rows: IPartnerFinSobRow[]
  /** Every pending action of the offering; only the `jbp-pat-sob` ones are read. */
  actions: IFinancialCockpitPendingAction[]
  offering: ProgramTypes
  /** `PartnerData.PartnerFlags`, the comma-separated flag names the catalog is matched against. */
  partnerFlags: string
  /** `PartnerData.SectionSettings`, parsed — the manual overrides of those flags. */
  sectionSettings: Partial<IClientSectionSettings>
  fYear: number
  /** The quarter SOB is collected for; `null` reads the whole year instead of one quarter. */
  fQuarter: number | null
}

interface ICatalogCategory {
  category: string
  order: number
}

/**
 * The categories of an offering that SOB actuals are collected on and this partner owes.
 *
 * Two catalog fields decide it, each read the way the rest of the app reads it. `IsSoBAvailable`
 * is where SOB is collected at all — the same field `buildSobAvailableSettingsByKey` builds the
 * SOB modal's editable rows from. `SOBMandatoryByFlags` is whether this partner owes it, matched
 * through the planning tab's own `isSubCategoryMandatory` rather than through a comparison of
 * ours: the field carries suffixes (`!`, `#`) and the `*` wildcard, and the partner's flags can be
 * overridden per section. `isSkipOverall` is `false` here because `*` — `everyone` — is exactly
 * how the catalog marks the categories every partner submits SOB for.
 *
 * Both are read across all of a category's rows rather than off its `jbp-overall` row alone: the
 * catalog states availability per hierarchy line, and a category whose SubCategory lines collect
 * SOB collects SOB, which is the reading `isPartnerFlagActive` takes of the same fields.
 */
const getCatalogCategories = ({
  settings,
  offering,
  partnerFlags,
  sectionSettings,
}: Pick<IParams, 'settings' | 'offering' | 'partnerFlags' | 'sectionSettings'>) => {
  const offeringName = offering.trim()
  const orders = new Map<string, number>()

  settings.forEach((setting) => {
    const category = setting.Category?.trim()

    if (
      !category ||
      category === OVERALL_CATEGORY ||
      setting.ProgramType?.trim() !== offeringName ||
      setting.IsMainHierarchy !== 1 ||
      setting.IsSoBAvailable !== 1
    ) {
      return
    }

    if (
      !isSubCategoryMandatory(
        partnerFlags,
        setting.SOBMandatoryByFlags ?? '',
        sectionSettings,
        false
      ).isMandatory
    ) {
      return
    }

    const order = orders.get(category)

    if (order === undefined || setting.CategoryOrder < order) {
      orders.set(category, setting.CategoryOrder)
    }
  })

  return Array.from(orders, ([category, order]): ICatalogCategory => ({ category, order }))
}

/**
 * Quarters of `fYear` whose actuals carry an SOB figure for the category.
 *
 * `null` is the payload's own word for a measure it did not send, so a figure is present when the
 * column came back as a number — zero included, which is a partner who sold nothing in a category
 * they still report on, not an unanswered one. The full-year row (`fQuarter` 5) is left out: it is
 * the total of the four quarters, so counting it would read a year as answered that no quarter is.
 */
const getQuartersWithSob = ({
  rows,
  offering,
  category,
  fYear,
}: {
  rows: IPartnerFinSobRow[]
  offering: ProgramTypes
  category: string
  fYear: number
}) => {
  const offeringName = offering.trim()
  const quarters = new Set<number>()

  rows.forEach((row) => {
    if (
      row.offering?.trim() !== offeringName ||
      row.category !== category ||
      row.fYear !== fYear ||
      row.fQuarter === FULL_YEAR_QUARTER ||
      !isActualsFinSobRow(row) ||
      typeof row.sob !== 'number' ||
      !Number.isFinite(row.sob)
    ) {
      return
    }

    quarters.add(row.fQuarter)
  })

  return Array.from(quarters).sort((left, right) => left - right)
}

/**
 * The categories behind a raised `SOB Actuals are missing`: what the offering collects SOB on,
 * minus what the partner has already submitted, each with the call that submits the rest.
 *
 * The server raised the status and this never re-decides that. What it answers is the question the
 * status leaves open — *which* categories — and the payload answers it in two halves that have to
 * be read together: `ActualsAndTargetsSettings` says where SOB is owed, `PartnerFinSobData` says
 * where a figure came in. Neither half is a list of what is missing; the difference between them
 * is.
 *
 * A `jbp-pat-sob` pending action is the server naming a category outright, so those are taken
 * first and kept even where the catalog does not name them — the server knows something the
 * catalog does not. The catalog then adds the categories no action covers, which is the case this
 * card exists for: SOB is collected outside the pending-action window too, and a partner who has
 * submitted nothing raises the status with no action to show for it.
 *
 * With no quarter to read against, the whole year is: a category counts as answered as soon as any
 * quarter of it carries a figure.
 */
export const getMissingSobCategories = ({
  settings,
  rows,
  actions,
  offering,
  partnerFlags,
  sectionSettings,
  fYear,
  fQuarter,
}: IParams): IMissingSobCategoriesResult => {
  const catalog = getCatalogCategories({ settings, offering, partnerFlags, sectionSettings })

  /* The payload's own order, the one the pending-actions tile lists its rows in, so a category
     does not change place between the tile and this card. */
  const sobActions = actions
    .filter(({ ActionType }) => ActionType === CommitmentReminderActionTypeEnum.JbpPatSob)
    .sort(
      (left, right) =>
        left.ActionSortOrder - right.ActionSortOrder || left.ActionOrder - right.ActionOrder
    )

  const orders = new Map(catalog.map(({ category, order }) => [category, order]))
  const entries = new Map<string, IMissingSobCategory>()

  sobActions.forEach((action) => {
    const category =
      resolvePlanReviewCategoryActionParam(normalizePlanReviewActionParams(action.ActionParam)) ??
      ''

    if (entries.has(category)) {
      return
    }

    entries.set(category, {
      category,
      /* An action naming a category the catalog does not carry still belongs in the list; it goes
         after the ones the catalog orders rather than jumping to the front. */
      order: orders.get(category) ?? Number.MAX_SAFE_INTEGER,
      action,
      quartersWithSob: category ? getQuartersWithSob({ rows, offering, category, fYear }) : [],
    })
  })

  catalog.forEach(({ category, order }) => {
    if (entries.has(category)) {
      return
    }

    const quartersWithSob = getQuartersWithSob({ rows, offering, category, fYear })
    const isAnswered =
      fQuarter === null ? quartersWithSob.length > 0 : quartersWithSob.includes(fQuarter)

    if (isAnswered) {
      return
    }

    entries.set(category, { category, order, action: null, quartersWithSob })
  })

  const categories = Array.from(entries.values()).sort(
    (left, right) => left.order - right.order || left.category.localeCompare(right.category)
  )

  if (categories.length > 0) {
    return { categories, state: 'missing' }
  }

  /* Nothing to list is two different facts, and the card has to say which. Either the payload
     never named a SOB category for this offering — then the status cannot be broken down at all —
     or every category it named is answered, and the server is raising the status on something the
     cockpit's own payload does not carry. */
  return { categories, state: catalog.length === 0 ? 'noCatalog' : 'allSubmitted' }
}
